import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Checkbox,
  CircularProgress,
  SxProps,
  Theme,
} from "@mui/material";
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Visibility as VisibilityIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Edit as EditIcon,
  Remove as RemoveIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslate } from "src/locales";
import SelectedStockRightPanel from "./SelectedStockRightPanel";
import ChoicesGridUpdate from "./choiesgrid-update";

export interface BaseStockData {
  id: number | string;
  [key: string]: any;
}

export interface PanelConfig {
  title: string;
  showTestDurations?: boolean;
  showPatientDebt?: boolean;
  showAddToPatient?: boolean;
  showVisibilityButton?: boolean;
  showFullScreenButton?: boolean;
  showEditButton?: boolean;
  customButtons?: Array<{
    label?: string;
    onClick: () => void;
    variant?: "outlined" | "contained" | "text";
    color?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
    size?: "small" | "medium" | "large";
    sx?: SxProps<Theme>;
    renderButton?: () => React.ReactNode;
  }>;
}

export interface ChoicesPanelProps<T extends BaseStockData> {
  data: T[];
  selectedItems: (number | string)[];

  config: PanelConfig;

  columns: GridColDef[];
  gridHeight?: string;
  gridPageSize?: number;

  onItemToggle: (itemId: number | string) => void;
  onSelectAll: () => void;
  onEdit?: (item: T) => void;
  onVisibilityClick?: () => void;
  onTestDurationsClick?: () => void;
  onPatientDebtClick?: () => void;
  onAddToPatientClick?: () => void;
  addToPatientDisabled?: boolean;
  isLoading?: boolean;

  backgroundColor?: string;
  headerBackgroundColor?: string;
  borderRadius?: number;
  shadow?: string;

  position?: "absolute" | "fixed" | "relative";
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  zIndex?: number;

  defaultExpanded?: boolean;
  defaultFullScreen?: boolean;

  renderCustomHeader?: (isExpanded: boolean) => React.ReactNode;
  renderCustomFooter?: (isExpanded: boolean) => React.ReactNode;

  disableExpansion?: boolean;
  disableFullScreen?: boolean;
  disableSelection?: boolean;

  // Optional row detail (expandable) support
  enableRowDetails?: boolean;
  detailAccessor?: string;
  detailColumns?: GridColDef[];
  detailPanelHeight?: number;

  renderContent?: (state: { isExpanded: boolean; isFullScreen: boolean }) => React.ReactNode;
  onExpandedChange?: (isExpanded: boolean) => void;
}

const ChoicesPanel = <T extends BaseStockData>({
  data,
  selectedItems,
  config,
  columns,
  gridHeight = "25vh",
  gridPageSize = 10,
  onItemToggle,
  onSelectAll,
  onEdit,
  onVisibilityClick,
  onTestDurationsClick,
  onPatientDebtClick,
  onAddToPatientClick,
  addToPatientDisabled = false,
  isLoading = false,
  backgroundColor = "background.paper",
  headerBackgroundColor = "#F4F7FE",
  borderRadius = 2,
  shadow = "0px -4px 20px rgba(0, 0, 0, 0.15), 0px 4px 20px rgba(0, 0, 0, 0.15)",
  position = "absolute",
  bottom = 16,
  left = 16,
  right = 16,
  zIndex = 1000,
  defaultExpanded = false,
  defaultFullScreen = false,
  renderCustomHeader,
  renderCustomFooter,
  disableExpansion = false,
  disableFullScreen = false,
  disableSelection = false,
  enableRowDetails = false,
  detailAccessor = "breakdown",
  detailColumns,
  detailPanelHeight = 140,
  renderContent,
  onExpandedChange,
}: ChoicesPanelProps<T>) => {
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isFullScreen, setIsFullScreen] = useState(defaultFullScreen);
  const [expandedRowIds, setExpandedRowIds] = useState<Array<number | string>>([]);

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const handleEditClick = (item: T) => {
    setEditingItem(item);
    setEditModalOpen(true);
    if (onEdit) onEdit(item);
  };

  const handleUpdateSave = (updatedData: T) => {
    // Burada props olarak gelen data'yı direkt değiştiremeyiz ama 
    // Şimdilik sadece onEdit'i çağırıyoruz veya parent'a haber veriyoruz.
    if (onEdit) onEdit(updatedData);
  };

  const handleToggleExpanded = () => {
    if (!disableExpansion) {
      const next = !isExpanded;
      setIsExpanded(next);
      if (onExpandedChange) {
        onExpandedChange(next);
      }
    }
  };

  const handleToggleFullScreen = () => {
    if (!disableFullScreen) {
      setIsFullScreen(!isFullScreen);
    }
  };

  const enhancedColumns: GridColDef[] = React.useMemo(() => {
    const baseColumns = [...columns];

    if (!disableSelection) {
      baseColumns.unshift({
        field: "select",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => {
          const allSelected = selectedItems.length > 0 && selectedItems.length === data.length;
          const someSelected = selectedItems.length > 0 && selectedItems.length < data.length;

          return (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={onSelectAll}
              checkedIcon={<CheckIcon sx={{ color: "white", fontSize: "12px" }} />}
              sx={{
                "&.Mui-checked": {
                  backgroundColor: "primary.main",
                  borderRadius: "4px",
                },
                "&.MuiCheckbox-indeterminate": {
                  backgroundColor: "warning.main",
                  borderRadius: "4px",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "14px",
                },
              }}
            />
          );
        },
        renderCell: (params) => {
          const isSelected = selectedItems.includes(params.row.id);
          return (
            <Checkbox
              checked={isSelected}
              onChange={() => onItemToggle(params.row.id)}
              checkedIcon={<RemoveIcon sx={{ color: "white", fontSize: "12px" }} />}
              sx={{
                "&.Mui-checked": {
                  backgroundColor: "primary.main",
                  borderRadius: "4px",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "14px",
                },
              }}
            />
          );
        },
      });
    }

    if (onEdit && config.showEditButton !== false) {
      baseColumns.push({
        field: "edit",
        headerName: t("patient-request-service.grid.columns.edit") || "Düzenle",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditClick(params.row)}
          >
            <EditIcon />
          </IconButton>
        ),
      });
    }

    return baseColumns;
  }, [
    columns,
    selectedItems,
    data,
    disableSelection,
    onEdit,
    config.showEditButton,
    onItemToggle,
    onSelectAll,
    t,
  ]);

  const getPanelHeight = () => {
    if (!isExpanded) return "60px";
    if (isFullScreen) return "80vh";
    return gridHeight;
  };

  return (
    <Box
      sx={{
        position,
        bottom,
        left,
        right,
        height: getPanelHeight(),
        backgroundColor,
        borderRadius,
        zIndex,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "none",
        boxShadow: shadow,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: isExpanded ? 1 : 0,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: headerBackgroundColor,
        }}
      >
        {/* Başlık kısmı - collapse/expand için tıklanabilir */}
        <Box
          onClick={disableExpansion ? undefined : handleToggleExpanded}
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            cursor: disableExpansion ? "default" : "pointer",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {config.title}
          </Typography>
        </Box>

        {/* Butonlar kısmı */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Stack direction="row" spacing={1}>
            {isExpanded && (
              <>
                {/* Custom buttons */}
                {config.customButtons?.map((button, index) =>
                  button.renderButton ? (
                    <Box key={index}>{button.renderButton()}</Box>
                  ) : (
                    <Button
                      key={index}
                      variant={button.variant || "outlined"}
                      size={button.size || "small"}
                      color={button.color || "primary"}
                      onClick={button.onClick}
                      sx={button.sx}
                    >
                      {button.label}
                    </Button>
                  )
                )}

                {/* Test durations button */}
                {config.showTestDurations && onTestDurationsClick && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={onTestDurationsClick}
                  >
                    {t("patient-request-service.selected-services.test-durations") || "Test Süreleri"}
                  </Button>
                )}

                {/* Patient debt button */}
                {config.showPatientDebt && onPatientDebtClick && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={onPatientDebtClick}
                  >
                    {t("patient-request-service.selected-services.patient-debt") || "Hasta Borcu"}
                  </Button>
                )}

                {/* Separator */}
                {(config.showTestDurations || config.showPatientDebt || config.showAddToPatient) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                    |
                  </Typography>
                )}

                {/* Add to patient button */}
                {config.showAddToPatient && onAddToPatientClick && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={onAddToPatientClick}
                    disabled={addToPatientDisabled}
                  >
                    {t("patient-request-service.selected-services.add-to-patient") || "Hastaya Ekle"}
                  </Button>
                )}

                {/* Visibility button */}
                {config.showVisibilityButton && onVisibilityClick && (
                  <IconButton size="small" onClick={onVisibilityClick}>
                    <VisibilityIcon />
                  </IconButton>
                )}

                {/* Fullscreen button */}
                {config.showFullScreenButton && !disableFullScreen && (
                  <IconButton
                    size="small"
                    onClick={handleToggleFullScreen}
                  >
                    {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                )}
              </>
            )}

            {/* Expand/collapse button */}
            {!disableExpansion && (
              <IconButton
                size="small"
                onClick={handleToggleExpanded}
              >
                {isExpanded ? (
                  <KeyboardArrowUpIcon sx={{ transform: "rotate(180deg)" }} />
                ) : (
                  <KeyboardArrowUpIcon />
                )}
              </IconButton>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Custom header renderer */}
      {renderCustomHeader && renderCustomHeader(isExpanded)}

      {/* Grid content or custom content */}
      {isExpanded && (
        renderContent ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {renderContent({ isExpanded, isFullScreen })}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              p: 2,
              overflow: "hidden",
              position: "relative",
              display: "flex",
              gap: 2,
            }}
          >
            {/* Sol: Grid (65%) */}
            <Box sx={{ width: "65%", minWidth: 0, height: "100%", overflow: "auto" }}>
              <ByzMuiDataGrid
                rows={data}
                columns={enhancedColumns}
                pageSize={gridPageSize}
                getRowId={(row) => row.id}
                checkboxSelection={false}
                disableColumnMenu={false}
                loading={isLoading}
                {...(data.length
                  ? {
                    getDetailPanelContent: (params: any) => {
                      const detailRows = Array.isArray(params.row?.detailRows)
                        ? params.row.detailRows
                        : [];

                      const formatNumber = (value: number | string | null | undefined) => {
                        if (value === null || value === undefined) return "0,00";
                        const num = typeof value === "string" ? parseFloat(value) : value;
                        if (isNaN(num)) return "0,00";
                        return new Intl.NumberFormat("tr-TR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(num);
                      };

                      const detailCols: GridColDef[] = [
                        { field: "kurumAdi", headerName: "Kurum Adı", flex: 1, minWidth: 160 },
                        { field: "fiyat", headerName: "Fiyat", width: 110, renderCell: (p) => formatNumber(p.value) },
                        { field: "indirimOrani", headerName: "İndirim Oranı", width: 120, renderCell: (p) => formatNumber(p.value) },
                        { field: "toplamTutar", headerName: "Toplam Tutar", width: 130, renderCell: (p) => formatNumber(p.value) },
                        { field: "indirimTutari", headerName: "İndirim Tutarı", width: 130, renderCell: (p) => formatNumber(p.value) },
                        { field: "kdv", headerName: "KDV", width: 90, renderCell: (p) => formatNumber(p.value) },
                        { field: "tevkifatTutar", headerName: "Tevkifat Tutar", width: 130, renderCell: (p) => formatNumber(p.value) },
                        { field: "tevOrani", headerName: "Tev. Oranı", width: 110, renderCell: (p) => formatNumber(p.value) },
                        { field: "netTutar", headerName: "Net Tutar", width: 110, renderCell: (p) => formatNumber(p.value) },
                      ];

                      return (
                        <Box sx={{ p: 1.5, width: "100%" }}>
                          <ByzMuiDataGrid
                            rows={detailRows.map((r: any, idx: number) => ({ ...r, id: r.id || `${params.row.id}-d-${idx}` }))}
                            columns={detailCols}
                            getRowId={(r: any) => r.id}
                            pageSize={detailRows.length || 10}
                            pagination={false}
                            autoHeight
                            checkboxSelection={false}
                            disableColumnMenu
                            sx={{
                              border: 0,
                              "& .MuiDataGrid-columnHeaders": {
                                display: "flex !important",
                                borderBottom: "1px solid #e0e0e0",
                                backgroundColor: "#f5f5f5",
                              },
                              "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 600,
                                fontSize: "0.875rem",
                              },
                              "& .MuiDataGrid-footerContainer": { display: "none" },
                              "& .MuiDataGrid-row": {
                                minHeight: "40px !important",
                                maxHeight: "40px !important",
                              },
                              "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                          />
                        </Box>
                      );
                    },
                    getDetailPanelHeight: () => 160,
                    getDetailPanelExpanded: (row: any) => expandedRowIds.includes(row.id),
                    onDetailPanelExpandedChange: (newExpandedIds: (number | string)[]) => {
                      setExpandedRowIds(newExpandedIds);
                    },
                  }
                  : {})}
                sx={{
                  autoHeight: false,
                  "& .MuiDataGrid-row": {
                    borderBottom: "1px solid #e0e0e0 !important",
                  },
                  "& .MuiDataGrid-main": {
                    overflow: "auto",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    overflow: "auto !important",
                  },
                  "& .MuiDataGrid-virtualScrollerContent": {
                    overflow: "auto !important",
                  },
                }}
              />
            </Box>

            {/* Sağ: %35 alan - kendi scroll'u olan Seçtikleriniz paneli */}
            <Box
              sx={{
                width: "35%",
                minWidth: 260,
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <SelectedStockRightPanel />
            </Box>
          </Box>
        )
      )}

      {/* Custom footer renderer */}
      {renderCustomFooter && renderCustomFooter(isExpanded)}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            zIndex: 2000,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={36} />
        </Box>
      )}

      {/* Edit Modal */}
      <ChoicesGridUpdate
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        data={editingItem}
        onSave={handleUpdateSave}
      />
    </Box>
  );
};

export default ChoicesPanel;

